import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, idempotencyKey } from "../api/client";
import { SKIP, useDrained, useQuery } from "../api/hooks";
import { useMutation } from "../api/mutation";
import { can } from "../authz";
import { FrozenNotice, useCarried } from "../carried";
import ChangeInDesign from "../shell/ChangeInDesign";
import { useFacets } from "../shell/facets";
import { ageOf, useReportedAge } from "../shell/freshness";
import { showsUnfinished } from "../shell/nav";
import { EntityMenuButton } from "../shell/EntityRef";
import { ScopeTrail } from "../shell/ScopeTrail";
import { ScopeLink, ScopeNavLink, useScope } from "../shell/scope";
import { useSession } from "../shell/session";
import { plural } from "../dashboard/format";
import { Empty, ProblemBox } from "../ui";
import ActPanel from "./devices/ActPanel";
import { ACTIONS, sendCommand, type Action, type Device, type Notice } from "./devices/commands";
import DeviceDetail, { useDeviceModel } from "./devices/DeviceDetail";
import DeviceHead, { DeviceSub } from "./devices/DeviceHead";
import { cdsListChipOf } from "./devices/CdsRecord";
import CustomerDevices from "./devices/CustomerDevices";
import { runningOf, useClients, useConvergence } from "./devices/DevicesTable";
import { designOf } from "./devices/family";
import { AccessPointsTable, ColumnChooser, FAMILY_MARKS, FamilySkeleton, SwitchesTable, useHiddenColumns, useLocations, useSwitchObservations, useWorkListRead, type AddressRead, type ApSortKey, type DeliveriesRead, type SwitchSortKey } from "./devices/FamilyTables";
import { useDeliveryChanges } from "./devices/delivery";
import FirmwareCard from "./devices/FirmwareCard";
import SiteUpgrade from "./devices/SiteUpgrade";
import Incomplete, { incompleteWords } from "./devices/Incomplete";
import { LIVENESS, matchesText, nextSort, type Liveness, type Sort } from "./devices/narrow";
import { usePlan } from "./devices/Links";
import { equipmentLabel } from "./devices/pieces";
import { openConsoleWindow } from "../console";
import { ListPage } from "./ListPage";

/** The firmware act's refusal reasons, whose Problem folds its server words (ADR-0413 `2a`). */
const FIRMWARE_REASONS = new Set<string>(["firmware_line_change", "unqualified_firmware", "firmware_delivery_in_flight", "firmware_delivery_not_handed_over"]);

/** The act waiting for its confirmation above the table, if any. */
type Panel = { kind: "act"; device: Device; action: Action };

/** How the list is being looked at (#1259, #834): each family's sort, and what
 * narrows the rows. Keyed by the site, so another site's list never arrives
 * under this one's narrowing — read as the default the moment the key changes,
 * as on Client devices. Nothing here is in the address or remembered. */
type View = { key: string; ap?: Sort<ApSortKey>; sw?: Sort<SwitchSortKey>; q: string; status: "" | Liveness; doc: "" | "current" | "not-current"; cds: boolean };
const fresh = (key: string): View => ({ key, q: "", status: "", doc: "", cds: false });
const LIVENESS_LABEL: Record<Liveness, string> = { online: "Online", offline: "Offline", unknown: "Unknown" };

/** Live devices of the site the scope picker shows, read from the site's cell
 * (ZENITH.md §9.4): online comes from the gateway and nothing else. Operate ›
 * Devices (#874). The site's topology was a section at this page's foot until
 * #874 gave it a page of its own, and since #1352 it is Operate › Wired's
 * Topology tab (`OperateTopology.tsx`), unchanged either time.
 *
 * **It reads, and it sends the device acts** (#875, ADR-0149 decisions 1 and
 * 2). What a device may be sent — blink, reboot, factory reset, the upgrade,
 * the console — stays here, because it is how an operator troubleshoots and
 * authors no intent. Claiming, importing, editing, deleting from the site and
 * putting a device in the plan are Design › Physical › Equipment's, and this
 * page says so with *Change this in Design* for a caller whose role may. */
export default function Devices() {
  const { me } = useSession();
  const { scope } = useScope();
  const siteId = scope.siteId;
  // The one device the address narrows the page to (#254). The list is asked
  // for it (#255): `serial` is the list's own parameter, so the page reads
  // the rows it shows and nothing is narrowed here. The context bar in the
  // rail names the device and clears it.
  const { facets } = useFacets();
  const serial = facets.serial;
  // **Two lists, one per family, each read with the list's own `category`**
  // (#878): paging and counts are per family and not a split of one page in
  // the browser. Both are read whichever tab is open — the access points' tab
  // needs the switches to say where each access point hangs, and the tabs say
  // both counts — and each is the one call per site §9.5 allows, through the
  // cell's read cache. The tab is a segment of the address, as Operate's and
  // Design's tabs are (#874): the access points are the page's first tab and
  // the switches are `/operate/devices/switches`. The address naming one
  // device (#254) reads that device alone, as before.
  const { pathname } = useLocation();
  const family: "ap" | "switch" = pathname.replace(/\/+$/, "").endsWith("/switches") ? "switch" : "ap";
  //
  // **Each family is read to its end** (#1259): every page, following
  // `next_page_token`, so the tabs, the counts and the rows are the site's and
  // not the first page's. The page size is the server's bound (ADR-0072); the
  // drain has none of its own, because how many devices a site holds is §29's
  // tenancy-limits question and is answered by reading the list whole.
  const list = (category?: "ap" | "switch") => (page_token?: string) => siteId
    ? api.GET("/sites/{id}/devices", { params: { path: { id: siteId }, query: { page_size: 200, ...(page_token ? { page_token } : {}), ...(serial ? { serial } : { category }) } } })
    : SKIP;
  const oneQ = useQuery(serial ? () => list()() : () => SKIP, [siteId, serial]);
  const apQ = useDrained(serial ? () => SKIP : list("ap"), [siteId, serial]);
  const swQ = useDrained(serial ? () => SKIP : list("switch"), [siteId, serial]);
  const q = serial ? oneQ : family === "switch" ? swQ : apQ;
  // **The address each device is reached at is read automatically for the
  // open family's online rows** (ADR-0443 amending ADR-0391, #1901). This is
  // its own async query: the rows above render from `apQ`/`swQ`, and the
  // Address column lazy-fills from `addressQ` when it lands, so the page never
  // blocks on the per-device fan-out. The read rides the per-device state cache
  // the client counts and switch port reads already fill, so a view within
  // `CONTROLLER_READ_CACHE_TTL` costs no controller call; a cold page pays at
  // most one read per online row. It is **not a display read** (ADR-0443
  // decision 1b was dropped: the async query already keeps the page
  // non-blocking, and a display read would need a `data_as_of` the list has no
  // field for, ADR-0232 decision 4). Still never stored (§11.2), still the open
  // family's online rows only, at most STATE_FAN_OUT in flight, the integrator's
  // `read_lan_address` unchanged. Asked per site and family, so switching either
  // reads the new one. There is no button: ADR-0391's opt-in *Read addresses*
  // control is gone, its cost accepted as documented in the amendment.
  const addressesAsked = !serial && !!siteId;
  const addressQ = useDrained(addressesAsked && siteId
    ? (page_token?: string) => api.GET("/sites/{id}/devices", { params: { path: { id: siteId }, query: { page_size: 200, ...(page_token ? { page_token } : {}), category: family, read_lan_address: true } } })
    : () => SKIP, [siteId, family, addressesAsked]);
  const addresses: AddressRead | undefined = useMemo(() => !addressesAsked ? undefined
    : addressQ.status === "error" ? { kind: "error" }
    : addressQ.status === "ready" ? { kind: "ready", by: new Map(addressQ.data.items.flatMap((d) => d.lan_address ? [[d.serial, d.lan_address] as const] : [])) }
    : { kind: "loading" }, [addressesAsked, addressQ.status, addressQ.data]);
  // Which of these devices run a configuration Zenith did not generate
  // (§11.2). There is no field on Device for it — Zenith stores no device —
  // so it is joined from readiness on the serial; see `carried.tsx`.
  const carried = useCarried(siteId);
  // What each device is actually running (§11.2, ADR-0022 decision 3). Its own
  // read rather than a field on Device, for the same reason as carried.
  const convergence = useConvergence(siteId);
  // How many client devices each access point reports (#248). Its own read,
  // once for the page: the cell is asked for each online access point's state
  // through its bounded fan-out, not once per row.
  const clients = useClients(siteId);
  const items: Device[] = q.data?.items ?? [];
  const aps: Device[] = apQ.data?.items ?? [];
  const switches: Device[] = useMemo(() => swQ.data?.items ?? [], [swQ.data]);
  // Why a site's list is empty, where a carry-over import says (ADR-0333
  // decision 2, #1110): the newest kept report, read only once the list has
  // answered with nothing, from the read the carry-over page makes.
  const emptyAps = !serial && family === "ap" && apQ.status === "ready" && aps.length === 0;
  const lastImport = useQuery(() => emptyAps && siteId
    ? api.GET("/sites/{id}/carry-over/imports", { params: { path: { id: siteId }, query: { page_size: 1 } } })
    : SKIP, [siteId, emptyAps]);
  const kept = lastImport.data?.items[0];
  const refusedAtImport = kept?.report.items.filter((x) => x.element === "device" && x.disposition === "refused").length ?? 0;
  // The device the page is about, when the address names one and the site
  // has it (#292, #329): the page's title is its name, its head is its state
  // and its acts, and the table is not drawn under it. Its own read — the one
  // that carries the catalogue model and the address (§9.5, #154) — is made
  // here once and shared by the head and the detail.
  const one = serial && items.length === 1 ? items[0] : undefined;
  const oneRead = useDeviceModel(one?.serial);
  // **Which access points a firmware delivery is under way for** (ADR-0403, #1813). On the list,
  // one read for the site from this service's own record (`2b`), never per row and never of the
  // rollout or the cell; on a device's page, the device's deliveries (ADR-0371 `d.1a`), for its
  // head and its body. Each is read again when the change stream says a delivery moved, and with
  // the page's own reload, so the marker follows a click without the panel open.
  const deliveriesOn = !serial && family === "ap";
  const siteDeliveries = useQuery(() => deliveriesOn && siteId
    ? api.GET("/sites/{id}/firmware-deliveries", { params: { path: { id: siteId } } })
    : SKIP, [siteId, deliveriesOn]);
  useDeliveryChanges(siteDeliveries.reload, deliveriesOn);
  const deliveries: DeliveriesRead | undefined = useMemo(() => !deliveriesOn ? undefined
    : siteDeliveries.status === "error" ? "unread"
    : siteDeliveries.data ? new Map(siteDeliveries.data.deliveries.map((x) => [x.serial, x.delivery] as const))
    : undefined, [deliveriesOn, siteDeliveries.status, siteDeliveries.data]);
  const oneDeliveriesOn = !!one && one.category !== "switch";
  const oneDeliveries = useQuery(() => oneDeliveriesOn && one
    ? api.GET("/devices/{serial}/firmware-deliveries", { params: { path: { serial: one.serial } } })
    : SKIP, [one?.serial, oneDeliveriesOn]);
  useDeliveryChanges(oneDeliveries.reload, oneDeliveriesOn);
  const newestDelivery = oneDeliveries.data?.deliveries[0];
  // How old what this page shows is, for the footer (#1315): the copy of the
  // cell's listing the rows come from (ADR-0234), and the clients read the
  // counts come from — a display read served within the stale bound
  // (ADR-0232 decision 4), whose age this page never said until the footer did —
  // and, on a device's page, the device's own read, a display read too since #1802.
  useReportedAge([...ageOf("listing", q.data?.listing_as_of), ...ageOf("figures", clients.data?.data_as_of), ...ageOf("figures", oneRead.read?.data_as_of)]);
  // An act can change either family (a reboot takes a device offline), so both are read again;
  // on a device's page, its own read is read again with its row (#1311).
  const reload = () => {
    if (serial) { oneQ.reload(); oneRead.reload(); if (oneDeliveriesOn) oneDeliveries.reload(); } else { apQ.reload(); swQ.reload(); if (deliveriesOn) siteDeliveries.reload(); }
  };
  // The columns this browser hides on the open family's table (UX-008).
  const [hiddenColumns, setHiddenColumns] = useHiddenColumns(family);
  const [panel, setPanel] = useState<Panel>();
  const viewKey = siteId ?? "";
  const [stored, setView] = useState<View>(() => fresh(viewKey));
  const view = stored.key === viewKey ? stored : fresh(viewKey);
  const update = (patch: Partial<View>) => setView({ ...view, ...patch });
  // A console opens in a window of its own (#537), so this page holds no
  // console state at all: no panel to scroll to (#370 is moot — there is
  // nothing on this page to bring into view), no focus to hand back, and no
  // "one at a time" rule. What bounds concurrency is the cell's own ceiling,
  // which refuses in the window that asked and says so there.
  const openConsole = (d: Device) => {
    if (openConsoleWindow(d.serial)) return;
    // A popup blocker is the ordinary cause, and it is silent: without this
    // the button appears to do nothing, which is the shape of #370 arriving
    // by a different route.
    setNotice({ text: `${d.name}\u2019s console could not be opened in a new window \u2014 the browser refused it. Allow windows from this page, then try again.` });
  };
  const [notice, setNotice] = useState<Notice>();
  // The page's one write lane, shared by the panels and the rows (#189).
  const m = useMutation();
  // The bulk ask's own lane (ADR-0522 decision 4), and the rows it names.
  const cdsAsk = useMutation();
  const [selected, setSelected] = useState<{ key: string; serials: Set<string> }>({ key: "", serials: new Set() });
  const row = { org_id: scope.orgId, customer_id: scope.customerId, site_id: siteId };
  const mayCommand = siteId ? can(me, "device.command", row) : false;
  // The PoE cycle is its own capability (ADR-0251), held today by the roles that
  // hold device.command (ruled 2026-10-04, #1369) and asked for by its own name.
  const mayPoeCycle = siteId ? can(me, "device.poe.cycle", row) : false;
  // The console is checked by the device's class, never by its configuration
  // source (ADR-0155 decision 4b): `device.console.switch` or
  // `device.console.ap`. No role holds either, so this is true only for a
  // superuser today (`can` answers true for one), and the API answers 404 to
  // everyone else.
  const mayConsoleOn = (category?: string) =>
    siteId ? can(me, category === "switch" ? "device.console.switch" : "device.console.ap", row) : false;
  // What Design › Physical › Equipment offers a device (#875): claiming,
  // editing and deleting from the site are `device.write`; putting it in the
  // plan is `planner.write` (ADR-0068 decision 8). A caller who holds either
  // is shown the way there, and nobody else is.
  const mayWrite = siteId ? can(me, "device.write", row) : false;
  const mayMaintain = mayWrite || (siteId ? can(me, "planner.write", row) : false);
  // Reading a switch's running configuration is its own capability and not the
  // console's (ADR-0155 decision 2b): a closed list of show lines, typed by Zenith.
  const mayReadRunning = siteId ? can(me, "device.cli.read", row) : false;
  // A switch's port intent, plan and apply are `network.write` (§11.3): the
  // ports are configuration, not the device record `device.write` covers. The
  // device's page reads them; this says whether it shows the way to Design.
  const mayConfigure = siteId ? can(me, "network.write", row) : false;
  // The device page's Audit tab (#388) is the Audit page's list narrowed to
  // the device, behind the same capability the nav hides that page on.
  const mayAudit = siteId ? can(me, "audit.read", row) : false;
  // The diagnostics tab is the superuser flag and not a capability at a
  // scope: `device.diagnostics.read` belongs to no role, and the read is 404
  // for everyone below the provider wherever they stand (ADR-0092 decision
  // 4). `can` would be the wrong question — it walks the scope tree, and
  // there is no scope in which a customer holds this.
  const mayDiagnose = !!me?.user.superuser;
  // A device's CDS record, its chip and its *CDS record…* in Actions, are drawn for a superuser alone
  // (Gabriel Provencher, 2026-10-29, #2118). The flag and not a capability, for drawing only: the device
  // read still answers `cds_record` as ADR-0457 and ADR-0494 decision 4 rule, and the re-read is still
  // `device.write`'s at the server.
  const showsCds = !!me?.user.superuser;
  // Asking the assistant about this device (#1864) is `ai.use` at its site
  // (ADR-0210 decision 5a): owner, admin and operator, not viewer. It is not
  // the Diagnostics tab above and does not share its gate — that window is
  // the provider's, and decision 3c refuses it as the model's input. The API
  // enforces `ai.use` and the device's scope itself; this decides what is drawn.
  // A deployment naming no language-model provider draws no panel at all: its
  // only answer would be `no_model_provider` (#2096; /me's `ai_assistant`),
  // and while the feature is unfinished it is drawn for a superuser alone (#2106).
  // That reads the same flag as the tab above for drawing only: the server still
  // answers `ai.use`, and the tab's read stays the flag's and no role's.
  const mayAskAssistant = siteId && me?.ai_assistant && showsUnfinished(me) ? can(me, "ai.use", row) : false;
  // What the two lists read beside the devices (#878), once for the page and
  // never per row (§9.5): each switch's live port state within the server's
  // bound — the ports, the PoE and both uplinks — and the site's work list for
  // the Design half of the document state, which is `network.write`'s to read.
  // The site's plan and its locations name where a bound planned device is
  // placed. All are reads, and the work list is the only one also made for a
  // page about one device: a switch's page says in its head whether Design
  // holds port intent no apply has carried (#1615), from the same read and the
  // same `designOf` the Switches list's Design line uses, never a second
  // definition of either. An access point's page does not ask: its venue's
  // word would be a read per page for a fact the access point list carries.
  const reads = useSwitchObservations(serial ? undefined : siteId, switches);
  const work = useWorkListRead(!serial || one?.category === "switch" ? siteId : undefined, mayConfigure);
  // The plan is read for both lists and for a device's own page (#1907): a device bound to a planned
  // device is named by the planned device's name on every page, as Design names it (`pieces.tsx`).
  const sitePlan = usePlan(siteId);
  const oneName = one ? equipmentLabel(one, sitePlan.data?.devices.find((d) => d.serial?.toLowerCase() === one.serial.toLowerCase())).label : undefined;
  const locations = useLocations(!serial && family === "ap" ? siteId : undefined);
  const open = (p: Panel | undefined) => { setNotice(undefined); m.clear(); setPanel(p); };
  const act = (d: Device, action: Action) => {
    open(undefined);
    if (action !== "poe_cycle" && action !== "firmware_request" && !ACTIONS[action].confirm) { void sendCommand(m, d, action).then((n) => { if (n) { setNotice(n); reload(); } }); return; }
    setPanel({ kind: "act", device: d, action });
  };
  const done = (n: Notice) => { setNotice(n); setPanel(undefined); reload(); };
  // The firmware act is the device page's Firmware card's (#1841), and only that page offers it.
  const upgrading = panel?.kind === "act" && panel.action === "firmware_request";
  if (!siteId) {
    // **With a customer and no site, the question has an answer** (ADR-0085):
    // the customer's devices across its sites, with every site that could not
    // be read named. Its own component, mounted here rather than folded into
    // this page: everything below this line is site-scoped, and decision 5
    // adds a read at the customer and no verb.
    if (scope.customerId) return <CustomerDevices customerId={scope.customerId} />;
    return (
      <>
        <div className="page-head"><h1>Devices</h1></div>
        {/* Restates ADR-0234 decision 1 (`1c`) — a list's session facts come from the copy of the
            controller's listing kept here, for a site's list and for a customer's; a device's own
            page is what reads the controller live. The sentence said both lists were "read live"
            until #1678 (ruled 2026-10-13, note `4005630701` item 1, in the words below). */}
        <Empty title="Choose a customer or a site in the scope switcher above." info="A site's devices are listed from the copy of its controller's listing kept here. A customer's devices are listed across all of its sites." />
      </>
    );
  }
  const online = [...aps, ...switches].filter((d) => d.online).length;
  const refresh = () => { reload(); clients.reload(); reads.reload(); work.reload(); };
  // A family whose read stopped at a failed page is counted as what was read
  // and said to be incomplete — never as the site's total.
  const short = (p?: { incomplete?: unknown }) => p?.incomplete ? ", incomplete" : "";
  const incomplete = !!(apQ.data?.incomplete || swQ.data?.incomplete);
  const counted = apQ.data && swQ.data;
  // The rows as looked at: narrowed, then ordered by the table. The counts in
  // the title and the tabs are about the family's whole list, and a filter
  // changes neither — a list short because of what was typed must never read
  // as a site with fewer devices.
  // **CDS not pointing here** (ADR-0522 decision 6, #2212), a superuser's (decision 5): the family's rows
  // whose CDS column is drawn, which is the API's reading and request on each row and nothing decided here.
  const cdsShown = showsCds ? items.filter((d) => !!cdsListChipOf(d)).length : 0;
  const cdsOn = showsCds && view.cds;
  const narrowed = view.q.trim() !== "" || view.status !== "" || view.doc !== "" || cdsOn;
  const docOk = convergence.status === "ready";
  const rows = items.filter((d) => matchesText(d, view.q)
    && (view.status === "" || d.online_state === view.status)
    && (view.doc === "" || !docOk || (view.doc === "current") === (runningOf(d.serial, convergence)?.word === "current"))
    && (!cdsOn || !!cdsListChipOf(d)));
  const noun = family === "switch" ? "switches" as const : "access points" as const;
  // The rows the bulk ask names: a superuser's selection, kept per site and family, and only rows the
  // family still lists.
  const selection = selected.key === `${viewKey}/${family}` ? selected.serials : new Set<string>();
  const select = (serials: Set<string>) => setSelected({ key: `${viewKey}/${family}`, serials });
  const chosen = items.filter((d) => selection.has(d.serial)).map((d) => d.serial);
  const cdsColumn = showsCds ? {
    selected: selection,
    onToggle: (serial: string) => { const next = new Set(selection); if (!next.delete(serial)) next.add(serial); select(next); },
    onToggleAll: (serials: string[], on: boolean) => { const next = new Set(selection); for (const x of serials) { if (on) next.add(x); else next.delete(x); } select(next); },
  } : undefined;
  // **Ask CDS to point these here**: the bulk ask (`POST /v1/sites/{id}/cutover:request`, ADR-0522
  // decision 4) naming the selected rows. It answers before each device's CDS record is read again, so
  // the page says what it will do per device and reads the lists again; each request is on its row then.
  const askCds = async () => {
    if (!siteId || chosen.length === 0) return;
    const id = siteId;
    const res = await cdsAsk.runWith(() => api.POST("/sites/{id}/cutover:request", {
      params: { path: { id }, header: { "Idempotency-Key": idempotencyKey() } },
      body: { serials: chosen },
    }));
    if (res.ok && res.data) {
      const devices = res.data.devices;
      const n = (a: string) => devices.filter((d) => d.action === a).length;
      setNotice({ text: `Asked for ${devices.length} ${plural(devices.length, "device")}: ${n("decide")} read again and decided, ${n("resend")} sent again, ${n("skip")} already in flight. Each device's request shows on its row once it is decided.` });
      select(new Set());
      reload();
    }
  };
  const rowProps = { convergence, work, busy: m.busy, mayCommand, mayConsole: mayConsoleOn(family), mayPoeCycle, onAct: act, onConsole: openConsole, addresses, cds: cdsColumn, hidden: hiddenColumns };
  const onSortAp = (k: ApSortKey) => update({ ap: nextSort(view.ap, k) });
  const onSortSwitch = (k: SwitchSortKey) => update({ sw: nextSort(view.sw, k) });
  // The first read is in flight and nothing is on screen yet (#1319): the
  // frame draws the family's skeleton where the table will be, and this page
  // holds the place of what stands around it — the count line under the
  // title, the toolbar — so that when the rows arrive they replace the
  // placeholders and nothing else moves. A `reload()` keeps `q.data`, so none
  // of this is drawn through a refresh; the rows stay (#1049's rule).
  const firstLoad = q.status === "loading" && !q.data;
  return (
    <ListPage title={oneName ?? "Devices"} query={q} loadingLabel="Loading the site's devices"
      // A page about one device says where it is, and that path is the scope (#2344).
      trail={one && <ScopeTrail siteId={one.site_id ?? siteId} />}
      titleMenu={one && <EntityMenuButton entity={{ kind: "device", serial: one.serial, name: oneName, mac: one.mac, address: one.lan_address ?? undefined, switch: one.category === "switch", siteId: one.site_id ?? siteId, siteName: one.site_name }} />}
      // What the list is and is not, behind the ⓘ beside the title (#1900). It was the two notes under
      // the table (#830), the second behind its own disclosure, and the table's legend (`FAMILY_MARKS`),
      // here in that order and word for word. The list's, and only the list's (#1801): it describes the
      // table's columns, so a page about one device, which draws no table, does not carry it.
      info={!serial && (
        <>
          {/* Restates ADR-0234 decisions 1 (`1c`) and 4 (`4a`), ZENITH.md §9.5 and §11.2, ADR-0295 — a list's
              session facts are Zenith's copy of the cell's listing, as old as the age the footer
              shows under the build (#1315); a device's own page reads the cell live; reports are
              read live and never stored. And ADR-0443 (amending ADR-0391, #1901) — the Address column
              is not the copy's: it is read automatically, by its own async query as the list loads,
              live from each online device's last report and never stored, which ADR-0391 keeps (#836). */}
          Online, last contact, firmware and model come from a copy of the controller's listing kept here, as old as the age shown under the build. Address is read automatically from each online device's last report. A device's own page reads the controller live, and reports are never stored.{" "}
          {/* TODO(copy): "the answer above" was written under the table, below the command's answer; it now sits
              in the title's ⓘ, above it. Kept word for word until it is reworded (web/COPY.md rule 8). */}
          A command goes to the device's controller, and the answer above is what the controller accepted. The address a device is reached at is read from its last state report, for the online devices on this list, automatically as the list loads and filled in beside each row as it answers. It is never stored. An offline device's last address is on its own page. Its management VLAN is not read anywhere yet.{" "}
          {FAMILY_MARKS}
        </>
      )}
      sub={one && q.data
        ? <DeviceSub device={one} cellId={q.data.cell_id} one={oneRead} showsCds={showsCds} />
        : q.data ? <>{serial ? <>The one device the address names</> : counted ? <>{aps.length + switches.length} {plural(aps.length + switches.length, "device")}{incomplete ? " read, list incomplete" : ""} ({aps.length} {plural(aps.length, "access point")}, {switches.length} {plural(switches.length, "switch", "switches")}) · {online} online</> : <>Loading the site's devices</>} · Controller <code>{q.data.cell_id}</code></>
          // The count line's place, held while the first read is in flight (#1319), so the
          // sentence does not push the tabs and the table down when it arrives.
          : !serial && firstLoad ? <span className="skeleton skeleton-text" aria-hidden="true" /> : undefined}
      head={one ? (
        <DeviceHead device={one} own={{ status: oneRead.matchStatus, read: oneRead.read }} convergence={convergence} busy={m.busy}
          design={one.category === "switch" ? designOf({ kind: "switch", serial: one.serial }, work) : undefined} designReady={work.kind === "ready"} delivery={newestDelivery}
          mayCommand={mayCommand} mayConsole={mayConsoleOn(one.category)} mayPoeCycle={mayPoeCycle} onRefresh={refresh} onAct={act} onConsole={openConsole}
          cds={showsCds ? { onReread: oneRead.reload } : undefined}
          change={mayMaintain && <ChangeInDesign to="/design/physical/equipment" facets={{ serial: one.serial }}
            title={`Edit ${oneName}, put it in the plan, or delete it from the site, in Design › Physical › Equipment.`} />} />
      ) : (
        // The controls alone. The counts sentence is the title's `sub` and no
        // longer shares this row (#830): `.page-head` is `flex-end` with
        // `flex-wrap`, so a head wide enough to wrap put the counts and the
        // primary action on the line *above* the `h1`, and the page read as
        // "Claim a device" 34px before it read as "Devices". The DOM order and
        // the screen reader's were always right; only the eye's was not.
        <span className="page-actions">
          {q.data && <><ScopeLink to="/operate/wireless">Client devices</ScopeLink> <button className="btn btn-quiet" onClick={refresh}>Refresh</button></>}
          {mayMaintain && q.data && <ChangeInDesign to="/design/physical/equipment" title="Claim or import devices, edit them, put them in the plan, or delete them from the site, in Design › Physical › Equipment." />}
        </span>
      )}
      panels={(
        <>
          {carried.carried.frozen && (
            <FrozenNotice carried={carried.carried.serials.size} unreadable={carried.carried.unreadable.size} exits={carried.carried.exits}>
              {" "}The access points it names are marked below. <ScopeLink to="/carry-over">Carry-over</ScopeLink> shows each one&rsquo;s
              state and what it still needs.
            </FrozenNotice>
          )}
          {panel?.kind === "act" && panel.action !== "firmware_request" && <ActPanel device={panel.device} action={panel.action} m={m} onDone={done} onCancel={() => { setPanel(undefined); m.clear(); }} />}
          {/* Restates ADR-0403 decision 1a and ADR-0413 decisions 5, 6, 7.3, 8 and 10 as amended 2026-10-17
              (#1841, #1844, #1845) — an access point's page has one Firmware card: the act when Upgrade
              opens it and no delivery is under way, the newest delivery otherwise (its progress, while one
              is under way, even with Upgrade pressed), and no notice beside it for a firmware answer. A
              refusal the provider may override is forced from that card in one click. Its × closes it in
              every state and changes nothing it sent; Upgrade brings it back. */}
          {one && oneDeliveriesOn && (
            <FirmwareCard key={one.serial} device={one} newest={newestDelivery} m={m} acting={upgrading}
              mayAct={mayCommand}
              onCloseAct={() => { setPanel(undefined); m.clear(); }}
              onAnswered={() => { setPanel(undefined); reload(); }} />
          )}
          {notice && (
            <div className="notice" role="status">
              {notice.text}{" "}
              {notice.action && <button type="button" className="btn btn-quiet" disabled={m.busy} onClick={notice.action.run}>{notice.action.label}</button>}
              <button type="button" className="btn btn-quiet" onClick={() => setNotice(undefined)}>Dismiss</button>
            </div>
          )}
          {/* The act panel shows the lane's Problem itself. */}
          {/* A firmware request refused with a Problem after its act closed lands here (ADR-0413
              decision 10), with its server words and request id under Details (decision 2, `2a`). The
              open act shows neither since #1839: it says a refusal in one line, and this box is outside
              it. An answer the server gave as a delivery is the Firmware card's (#1841). */}
          {panel === undefined && m.problem && <ProblemBox problem={m.problem} onDismiss={m.clear} fold={FIRMWARE_REASONS.has(m.problem.reason)} />}
        </>
      )}
      failure={(p) => p.reason === "unresolved" ? (
        <Empty title="This site has no controller yet." info="A superuser registers one at a terminal with the cell subcommand; until then nothing can be read for it." />
      ) : p.reason === "upstream" ? (
        <>
          <ProblemBox problem={{ ...p, message: "The controller did not answer." }} />
          <p className="muted">{p.message}</p>
          <button className="btn" onClick={reload}>Try again</button>
        </>
      ) : <ProblemBox problem={p} />}
      before={!serial && (
        <>
          <nav className="tabs" aria-label="Devices tabs">
            <ScopeNavLink to="/operate/devices" end>Access points{apQ.data && ` (${aps.length}${short(apQ.data)})`}</ScopeNavLink>
            <ScopeNavLink to="/operate/devices/switches" end>Switches{swQ.data && ` (${switches.length}${short(swQ.data)})`}</ScopeNavLink>
          </nav>
          {family === "ap" && <Incomplete problem={apQ.data?.incomplete} read={aps.length} noun="access points" onRetry={apQ.reload} />}
          {/* A site's firmware upgrade run (ADR-0463, #1967), on the access points' tab: its newest run
              and, for a caller who may command devices, the Upgrade site panel. */}
          {family === "ap" && <SiteUpgrade siteId={siteId} mayCommand={mayCommand} />}
          {family === "switch" && <Incomplete problem={swQ.data?.incomplete} read={switches.length} noun="switches" onRetry={swQ.reload} />}
          {/* Above the table from the first paint (#1319), and live rather than inert: what is
              typed or chosen while the rows are read narrows them when they arrive, as a sort
              pressed on the skeleton's header orders them. It is in `before` rather than with
              the rows because the frame renders `before`, then the skeleton, then `children`:
              drawn with the rows it would sit under the skeleton and above the table, in the
              DOM order the frame renders, and so move when the rows arrive. */}
          {showsCds && chosen.length > 0 && (
            <div className="toolbar" role="group" aria-label="Selected devices">
              <span>{chosen.length} selected</span>
              {/* Restates ADR-0522 decisions 3, 4 and 5 and ADR-0503 decision 5c (#2212) — a superuser's ask, per
                  device and with no site gate: each device's CDS record is read again and its request decided,
                  one already in flight is skipped, and every request is an onboarding one. */}
              <button type="button" className="btn btn-primary" disabled={cdsAsk.busy} onClick={() => void askCds()}>
                {cdsAsk.busy ? "Asking…" : "Ask CDS to point these here"}
              </button>
              {rows.length > chosen.length && (
                <button type="button" className="btn btn-quiet" onClick={() => cdsColumn?.onToggleAll(rows.map((d) => d.serial), true)}>
                  Select all {rows.length} shown
                </button>
              )}
              <button type="button" className="btn btn-quiet" onClick={() => select(new Set())}>Clear selection</button>
              <span className="muted">Each device is read again, then asked. One already in flight is skipped.</span>
            </div>
          )}
          {cdsAsk.problem && <ProblemBox problem={cdsAsk.problem} onDismiss={cdsAsk.clear} />}
          {(items.length > 0 || firstLoad) && (
            <div className="toolbar">
              <input aria-label={`Filter ${noun}`} placeholder="Name, serial, model or firmware" value={view.q}
                onChange={(e) => update({ q: e.target.value })} autoComplete="off" spellCheck={false} style={{ width: 280 }} />
              {/* Restates ADR-0219 decisions 1 and 3 — the three answers `online_state` is served
                  with, offered as given: `unknown` is a state of its own and not a kind of offline. */}
              <select aria-label="Status" value={view.status} onChange={(e) => update({ status: e.target.value as View["status"] })}>
                <option value="">Any status</option>
                {LIVENESS.map((l) => <option key={l} value={l}>{LIVENESS_LABEL[l]}</option>)}
              </select>
              <select aria-label="Configuration" value={view.doc} disabled={!docOk} onChange={(e) => update({ doc: e.target.value as View["doc"] })}
                title={docOk ? "Filters on the Device line of the Configuration column." : "The Device line has not been read for this site, so there is nothing to filter on."}>
                <option value="">Any configuration</option>
                <option value="not-current">Not current</option>
                <option value="current">Current</option>
              </select>
              {/* Restates ADR-0522 decision 6 (#2212) — the filter is drawn only while a row's CDS record does
                  not point here, and narrows to the rows whose CDS column is drawn. */}
              {showsCds && cdsShown > 0 && (
                <button type="button" className={cdsOn ? "btn btn-sm btn-primary" : "btn btn-sm btn-quiet"} aria-pressed={cdsOn}
                  onClick={() => update({ cds: !view.cds })}>CDS not pointing here ({cdsShown})</button>
              )}
              {narrowed && items.length > 0 && <span className="muted">{rows.length} of {incomplete ? `the ${items.length} read` : items.length} shown</span>}
              {(narrowed || (family === "switch" ? view.sw : view.ap)) && (
                <button type="button" className="btn btn-quiet" onClick={() => setView(fresh(viewKey))}>Clear</button>
              )}
              <ColumnChooser family={family} hidden={hiddenColumns} onChange={setHiddenColumns} />
            </div>
          )}
        </>
      )}
      skeleton={!serial && (family === "switch"
        ? <FamilySkeleton family="switch" sort={view.sw} onSort={onSortSwitch} cds={showsCds} hidden={hiddenColumns} />
        : <FamilySkeleton family="ap" sort={view.ap} onSort={onSortAp} cds={showsCds} hidden={hiddenColumns} />)}
      empty={serial
        ? { when: items.length === 0, title: "No such device at this site.",
          action: <><ScopeLink to="/operate/devices" facets={{}}>All devices</ScopeLink> of the site.</> }
        : { when: items.length === 0, title: family === "switch" ? "No switches claimed to this site." : "No access points claimed to this site.",
          // Restates ADR-0333 decisions 2 and 3 — corrected with the record: where the site's
          // newest carry-over import refused devices, claiming is not what they need, so the
          // list says what the import said and where, rather than offering a claim. It says
          // what the import reported when it was committed, which is what the record holds.
          // Why it is empty is the ⓘ beside the line, and the one way on is its action (#1900); the
          // Carry-over link that sat inside the sentence is that action, so the sentence names the page.
          ...(family === "ap" && kept && refusedAtImport > 0
            ? { info: <>The carry-over import <strong>{kept.import_ref}</strong> refused {refusedAtImport} {plural(refusedAtImport, "device")}, so {refusedAtImport === 1 ? "it was not" : "none was"} claimed to this site. Claiming {refusedAtImport === 1 ? "it" : "one"} here is not the remedy: Carry-over keeps that import&rsquo;s report and says why each was refused.</>,
              action: <ScopeLink to="/carry-over">Carry-over</ScopeLink> }
            : q.data?.venue_id
            ? (mayWrite ? { action: <>Claim the first device on <ScopeLink to="/design/physical/equipment" facets={{}}>Design › Physical › Equipment</ScopeLink>.</> } : { info: "The site's venue has no inventory on the controller." })
            // Restates ADR-0493 decision 1 (`1f`), built by #2105 — the first claim creates the venue,
            // for someone who may change the site, so the Sites page is no longer the only way in.
            : { info: "The site has no venue on its controller yet. Claiming its first device in Design › Physical › Equipment creates it, for someone who may change the site; Create the venue on the Sites page does too." }) }}>
      {/* The address names one device (#254): the page is about it (#292),
          headed by it (#329), and the table is not drawn — its state and its
          acts are in the head, and the two columns only it carried, the
          configuration's source and the client count, are facts in the detail. */}
      {one && (
        <DeviceDetail key={one.serial} device={one} name={oneName} siteId={siteId} mayConfigure={mayConfigure} mayMaintain={mayMaintain} mayConsole={mayConsoleOn(one.category)} mayReadRunning={mayReadRunning} mayAudit={mayAudit} mayDiagnose={mayDiagnose} mayAskAssistant={mayAskAssistant}
          one={oneRead} carried={carried} clients={clients} convergence={convergence} onConsole={openConsole}
          // The Firmware fact's upgrade entries open the same Firmware card the Actions menu's
          // Upgrade firmware… opens (#1889), through the same act; its history is this page's read.
          firmware={oneDeliveriesOn ? {
            mayUpgrade: mayCommand, busy: m.busy, onUpgrade: () => act(one, "firmware_request"),
            deliveries: oneDeliveries.status === "error" ? "unread" : oneDeliveries.data?.deliveries,
          } : undefined} />
      )}
      {reads.readers}
      {!one && !serial && items.length > 0 && rows.length === 0 && (
        <p>No {family === "switch" ? "switch" : "access point"} matches. {incomplete ? incompleteWords(items.length, noun) : `The site has ${items.length};`} the filter hides {plural(items.length, "it", "all of them")}.</p>
      )}
      {!one && !serial && rows.length > 0 && (family === "switch"
        ? <SwitchesTable items={items} rows={rows} sort={view.sw} onSort={onSortSwitch}
          observed={reads.observed} planned={sitePlan.status === "error" ? "unread" : sitePlan.data?.devices} {...rowProps} />
        : <AccessPointsTable items={items} rows={rows} sort={view.ap} onSort={onSortAp}
          switches={switches} observed={reads.observed}
          planned={sitePlan.status === "error" ? "unread" : sitePlan.data?.devices} locations={locations.status === "error" ? "unread" : locations.data?.locations}
          clients={clients} carried={carried} deliveries={deliveries} {...rowProps} />)}
    </ListPage>
  );
}
